/**
 * A remembered choice, per browser — *not* a preference.
 *
 * 009 FR-014: "preference" names exactly one thing in this application, a stored per-practitioner
 * setting (`core/preferences/`). What lives here is per-browser UI memory — the home screen's
 * grouping choice and its collapsed groups — and it was called `preference.ts` until two concepts
 * shared one word at every call site. The **storage keys are deliberately unchanged** by that
 * rename: renaming them would silently discard every practitioner's remembered grouping.
 *
 * Browser storage can throw outright — private mode, blocked site data, an embedded viewer — so
 * every read and write is guarded and a failure degrades to "not remembered" rather than breaking
 * the page (the same rule `session.store.ts` follows for the refresh token).
 */
export function readUiState<T>(key: string, isValid: (value: unknown) => value is T): T | null {
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return null;
    const parsed: unknown = JSON.parse(raw);
    return isValid(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export function writeUiState(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* a choice we cannot remember is still a choice we can honour for this page load */
  }
}
